import React, { Component } from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AssistantMark } from './AssistantMark';
interface ChatMessageProps {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
}
function formatTime(date: Date): string {
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  });
}
/** Tasteful prose styling for assistant markdown — monochrome, on-brand. */
const markdownComponents = {
  p: ({
    children
  }: any) => <p className="text-[15px] leading-relaxed text-white/85 mb-3 last:mb-0" data-id="element-201" data-mp="2thtuhc34df1v">
      {children}
    </p>,
  strong: ({
    children
  }: any) => <strong className="font-semibold text-white" data-id="element-202" data-mp="2xidtkl796sqn">{children}</strong>,
  em: ({
    children
  }: any) => <em className="italic text-white/90" data-id="element-203" data-mp="32jl0ki1knnw9">{children}</em>,
  a: ({
    children,
    href
  }: any) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-white underline underline-offset-2 decoration-white/40 hover:decoration-white transition-colors" data-id="element-204" data-mp="37io5m2hjxu48">
      {children}
    </a>,
  ul: ({
    children
  }: any) => <ul className="list-disc pl-5 mb-3 last:mb-0 space-y-1.5 marker:text-white/40" data-id="element-205" data-mp="39ds2vohz988h">
      {children}
    </ul>,
  ol: ({
    children
  }: any) => <ol className="list-decimal pl-5 mb-3 last:mb-0 space-y-1.5 marker:text-white/40" data-id="element-206" data-mp="30ey7u2kaopx1">
      {children}
    </ol>,
  li: ({
    children
  }: any) => <li className="text-[15px] leading-relaxed text-white/85" data-id="element-207" data-mp="yrvgha2uycq">{children}</li>,
  h1: ({
    children
  }: any) => <h3 className="text-base font-semibold text-white mb-2 mt-1" data-id="element-208" data-mp="3t3vmzwt7m6lk">{children}</h3>,
  h2: ({
    children
  }: any) => <h3 className="text-base font-semibold text-white mb-2 mt-1" data-id="element-209" data-mp="3oa0woaj3mtdi">{children}</h3>,
  h3: ({
    children
  }: any) => <h4 className="text-sm font-semibold text-white mb-1.5 mt-1" data-id="element-210" data-mp="knaaw75a6xe8">{children}</h4>,
  code: ({
    children
  }: any) => <code className="px-1.5 py-0.5 rounded-md bg-white/10 text-white text-[13px] font-mono" data-id="element-211" data-mp="fwtvwiltb3pr">
      {children}
    </code>,
  blockquote: ({
    children
  }: any) => <blockquote className="border-l-2 border-white/20 pl-3 italic text-white/70 my-3" data-id="element-212" data-mp="yuvsh0v0oc5m">
      {children}
    </blockquote>
};
export function ChatMessage({
  role,
  content,
  timestamp,
  isStreaming = false
}: ChatMessageProps) {
  const isUser = role === 'user';
  if (isUser) {
    // User turn — right-aligned, contained neutral surface, plain text.
    return <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      ease: [0.23, 1, 0.32, 1]
    }} className="group flex flex-col items-end" data-id="element-213" data-mp="g19ppia1fqlo">
        <div className="max-w-[85%] rounded-2xl rounded-br-md bg-white text-slate-900 px-4 py-2.5" data-id="element-214" data-mp="73fwig7kld2q">
          <p className="text-[15px] leading-relaxed whitespace-pre-wrap" data-id="element-215" data-mp="b3hykqh7w8ij">
            {content}
          </p>
        </div>
        <span className="mt-1 mr-1 text-[11px] text-white/30 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-216" data-mp="21dt7oikcw7w0">
          {formatTime(timestamp)}
        </span>
      </motion.div>;
  }
  // Assistant turn — full-width, no bubble, small leading mark, markdown body.
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    ease: [0.23, 1, 0.32, 1]
  }} className="group flex gap-3" data-id="element-217" data-mp="3lzjalragzv5g">
      <AssistantMark size={28} className="mt-0.5" data-id="element-218" data-mp="3uwtjj5ztf877" />
      <div className="flex-1 min-w-0 pt-0.5" data-id="element-219" data-mp="dhde34txyjdz">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents} data-id="element-220" data-mp="dg1z08v1pp7k">
          {content}
        </ReactMarkdown>
        {isStreaming && <motion.span animate={{
        opacity: [0, 1, 0]
      }} transition={{
        duration: 1,
        repeat: Infinity
      }} className="inline-block w-1.5 h-4 ml-0.5 bg-white align-middle rounded-sm" aria-hidden="true" data-id="element-221" data-mp="mib7kt6r78ib" />}
        <span className="mt-1 block text-[11px] text-white/30 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-222" data-mp="2oapnxntng4wm">
          {formatTime(timestamp)}
        </span>
      </div>
    </motion.div>;
}